import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CalendarX, Check, AlertCircle } from "lucide-react";

const API = "https://api.komorotel.com/api";

/* 🆕 ANNULATION AVANT VERSEMENT — page du lien reçu dans les instructions
   de paiement d'un forfait.

   Presque personne ne crée de compte sur Komorotel. Sans cette page, un
   client qui ne voulait plus de son séjour n'avait aucun moyen de libérer
   ses dates : 7 à 30 nuits restaient bloquées jusqu'à l'échéance du
   versement, invendables, alors que personne n'en voulait.

   La page demande une confirmation au lieu d'annuler à l'ouverture. Les
   aperçus et les antivirus des messageries suivent les liens des mails
   sans que personne n'ait rien demandé : un lien qui annule tout seul
   finirait par annuler des séjours que le client voulait garder. */

const fmt = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const fmtHeure = (d) =>
  new Date(d).toLocaleString("fr-FR", {
    day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
  });

export default function AnnulationVersement() {
  const { token } = useParams();

  const [resa, setResa]     = useState(null);
  const [etat, setEtat]     = useState("chargement"); // chargement | prete | annulee | erreur
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi]   = useState(false);

  useEffect(() => {
    axios.get(`${API}/reservation/deposit/${token}`)
      .then((r) => { setResa(r.data); setEtat("prete"); })
      .catch((err) => {
        setErreur(err.response?.data?.error || "Ce lien n'est plus valable.");
        setEtat("erreur");
      });
  }, [token]);

  const annuler = async () => {
    setEnvoi(true);
    try {
      await axios.post(`${API}/reservation/deposit/${token}/cancel`);
      setEtat("annulee");
    } catch (err) {
      setErreur(err.response?.data?.error || "L'annulation n'a pas pu être enregistrée.");
      setEtat("erreur");
    } finally {
      setEnvoi(false);
    }
  };

  if (etat === "chargement") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div
          className="w-8 h-8 rounded-full animate-spin"
          style={{ border: "3px solid #e5e7eb", borderTopColor: "var(--k-green)" }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-start justify-center px-5 py-12 sm:py-20">
      <div className="w-full max-w-[460px]">

        {etat === "erreur" && (
          <>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-red-50">
              <AlertCircle size={20} className="text-red-500" />
            </div>
            <h1 className="text-[22px] font-bold text-gray-900 mb-2">Lien inutilisable</h1>
            <p className="text-[14px] text-gray-500 leading-relaxed">{erreur}</p>
          </>
        )}

        {etat === "annulee" && (
          <>
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
              style={{ background: "var(--k-green-soft)" }}
            >
              <Check size={20} style={{ color: "var(--k-green)" }} />
            </div>
            <h1 className="text-[22px] font-bold text-gray-900 mb-2">Réservation annulée</h1>
            <p className="text-[14px] text-gray-500 leading-relaxed">
              Vos dates sont libérées et rien ne vous est facturé, puisque aucun
              règlement n'avait été encaissé. L'établissement a été prévenu.
            </p>
            <Link
              to="/hotels"
              className="inline-block mt-7 px-5 py-2.5 rounded-xl text-white text-[14px] font-semibold"
              style={{ background: "var(--k-green)" }}
            >
              Chercher un autre hébergement
            </Link>
          </>
        )}

        {etat === "prete" && resa && !resa.annulable && (
          <>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-gray-100">
              <AlertCircle size={20} className="text-gray-400" />
            </div>
            <h1 className="text-[22px] font-bold text-gray-900 mb-2">
              {resa.status === "cancelled" ? "Déjà annulée" : "Séjour déjà validé"}
            </h1>
            <p className="text-[14px] text-gray-500 leading-relaxed">
              {resa.status === "cancelled"
                ? "Cette réservation a déjà été annulée, vos dates sont libres."
                : `Votre règlement a été enregistré par ${resa.hotelName || "l'établissement"}, ce lien ne s'applique donc plus. Contactez l'établissement pour toute modification.`}
            </p>
          </>
        )}

        {etat === "prete" && resa?.annulable && (
          <>
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-amber-50">
              <CalendarX size={20} className="text-amber-600" />
            </div>

            <h1 className="text-[22px] font-bold text-gray-900 mb-2">
              Libérer vos dates ?
            </h1>
            <p className="text-[14px] text-gray-500 leading-relaxed mb-7">
              Rien n'a été réglé, donc rien ne vous sera facturé. Vos dates
              repartent à la vente et quelqu'un d'autre pourra les réserver.
            </p>

            <div className="rounded-2xl border border-gray-200 overflow-hidden mb-7">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="text-[15px] font-bold text-gray-900">
                  {resa.hotelName}
                  {resa.city && <span className="font-normal text-gray-400"> · {resa.city}</span>}
                </div>
                {resa.roomTitle && (
                  <div className="text-[13px] text-gray-500 mt-0.5">{resa.roomTitle}</div>
                )}
              </div>

              <Ligne label="Arrivée" valeur={fmt(resa.checkInDate)} />
              <Ligne label="Départ"  valeur={fmt(resa.checkOutDate)} />
              <Ligne label="Durée"   valeur={`${resa.totalNights} nuits`} />
              <Ligne label="Référence" valeur={resa.reservationId} mono />
            </div>

            {resa.dueAt && (
              <p className="text-[13px] text-gray-400 leading-relaxed mb-5">
                Si vous ne faites rien, vos dates repartiront d'elles-mêmes à la
                vente après le {fmtHeure(resa.dueAt)}.
              </p>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={annuler}
                disabled={envoi}
                className="px-5 py-3 rounded-xl text-white text-[14px] font-semibold disabled:opacity-60"
                style={{ background: "#B4232A" }}
              >
                {envoi ? "Annulation…" : "Oui, annuler ma réservation"}
              </button>
              <Link
                to="/hotels"
                className="px-5 py-3 rounded-xl text-center text-[14px] font-semibold text-gray-700 border border-gray-200"
              >
                Non, je garde mon séjour
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Ligne({ label, valeur, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3 border-b border-gray-100 last:border-0">
      <span className="text-[13px] text-gray-400 shrink-0">{label}</span>
      <span
        className={`text-[13.5px] text-gray-900 text-right ${mono ? "font-mono text-[12.5px]" : "font-medium"}`}
      >
        {valeur}
      </span>
    </div>
  );
}
